import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { X, Save, Package, TrendingUp, RefreshCw, Trash2 } from "lucide-react";

/**
 * TransactionForm - Process stock transactions
 *
 * This is the ONLY way to manually change stock quantity.
 * All transactions are recorded with full audit trail.
 *
 * Transaction types:
 * - receive: Add stock (incoming)
 * - adjustment: Correct stock count (can be +/-)
 * - waste: Dispose of damaged/expired stock
 *
 * NOTE: Dispensing is handled through the Prescription flow only.
 */
export default function TransactionForm({ item, onSave, onClose }) {
  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      transactionType: "",
      quantity: "",
      unitCost: "",
      reason: "",
      notes: "",
    },
  });

  // Pre-populate from inventory item
  useEffect(() => {
    if (item) {
      setValue("unitCost", item.unitCost || "");
    }
  }, [item, setValue]);

  const transactionType = watch("transactionType");
  const quantity = watch("quantity");

  // Calculate what the new stock will be
  const calculateNewStock = () => {
    if (!item || !transactionType || quantity === "" || quantity === "-") return null;

    const currentStock = item.currentStock || 0;
    const qty = parseInt(quantity) || 0;

    switch (transactionType) {
      case "receive":
        return currentStock + Math.abs(qty);
      case "waste":
        return currentStock - Math.abs(qty);
      case "adjustment":
        return currentStock + qty; // qty can be negative
      default:
        return null;
    }
  };

  const newStock = calculateNewStock();
  const quantityNum = quantity === "" || quantity === "-" ? 0 : parseInt(quantity) || 0;
  const isValidTransaction =
    transactionType &&
    quantity !== "" &&
    quantity !== "-" &&
    quantityNum !== 0 &&
    (newStock === null || newStock >= 0);

  // Get icon for transaction type
  const getTransactionIcon = (type) => {
    switch (type) {
      case "receive":
        return <TrendingUp className="w-4 h-4 text-green-600" />;
      case "adjustment":
        return <RefreshCw className="w-4 h-4 text-amber-600" />;
      case "waste":
        return <Trash2 className="w-4 h-4 text-red-600" />;
      default:
        return null;
    }
  };

  const onSubmit = (data) => {
    const qty = parseInt(data.quantity) || 0;

    // Build transaction data for API
    const transactionData = {
      itemId: item.id,
      transactionType: data.transactionType,
      quantity: data.transactionType === "adjustment" ? qty : Math.abs(qty),
      unitCost: parseFloat(data.unitCost) || item.unitCost || 0,
      reason: data.reason,
      notes: data.notes,
    };

    onSave(transactionData);
  };

  if (!item) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl bg-white/90 backdrop-blur-xl border-slate-200/60 shadow-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-white/80 backdrop-blur-sm z-10 border-b">
          <CardTitle>Stock Transaction</CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Item Info (Read-only) */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Package className="w-5 h-5 text-slate-600 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900">{item.name}</h3>
                  <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <p className="text-slate-500">Batch</p>
                      <p className="font-medium text-slate-900">{item.batchNumber}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Current Stock</p>
                      <p className="font-medium text-slate-900">{item.currentStock}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Unit Cost</p>
                      <p className="font-medium text-slate-900">R{Number(item.unitCost || 0).toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Expiry</p>
                      <p className="font-medium text-slate-900">
                        {item.expiryDate
                          ? new Date(item.expiryDate).toLocaleDateString()
                          : "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Transaction Type */}
            <div className="space-y-2">
              <Label>Transaction Type *</Label>
              <Controller
                name="transactionType"
                control={control}
                rules={{ required: "Transaction type is required" }}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select transaction type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="receive">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-green-600" />
                          <span>Receive Stock</span>
                          <span className="text-slate-400 text-xs">(+)</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="adjustment">
                        <div className="flex items-center gap-2">
                          <RefreshCw className="w-4 h-4 text-amber-600" />
                          <span>Stock Adjustment</span>
                          <span className="text-slate-400 text-xs">(+/-)</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="waste">
                        <div className="flex items-center gap-2">
                          <Trash2 className="w-4 h-4 text-red-600" />
                          <span>Waste/Disposal</span>
                          <span className="text-slate-400 text-xs">(-)</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.transactionType && (
                <p className="text-sm text-red-600">{errors.transactionType.message}</p>
              )}
            </div>

            {/* Quantity and Cost */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="quantity">
                  Quantity *
                  {transactionType === "adjustment" && (
                    <span className="text-slate-400 text-xs ml-1">
                      (use negative to decrease)
                    </span>
                  )}
                </Label>
                <Controller
                  name="quantity"
                  control={control}
                  rules={{
                    required: "Quantity is required",
                    validate: (value) => {
                      if (value === "" || value === "-") return "Enter a valid quantity";
                      const num = parseInt(value);
                      if (isNaN(num) || num === 0) return "Quantity cannot be zero";
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      id="quantity"
                      type="text"
                      inputMode="numeric"
                      placeholder="Enter quantity"
                      value={field.value}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty, minus sign, or valid numbers (including negative)
                        if (value === "" || value === "-" || /^-?\d+$/.test(value)) {
                          field.onChange(value);
                        }
                      }}
                      autoComplete="off"
                    />
                  )}
                />
                {errors.quantity && (
                  <p className="text-sm text-red-600">{errors.quantity.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="unitCost">Unit Cost (ZAR)</Label>
                <Input
                  id="unitCost"
                  type="text"
                  inputMode="decimal"
                  {...register("unitCost")}
                  autoComplete="off"
                />
                <p className="text-xs text-slate-500">
                  Pre-filled from item. Update if cost changed.
                </p>
              </div>
            </div>

            {/* Stock Preview */}
            {transactionType && quantity !== "" && quantity !== "-" && quantityNum !== 0 && (
              <div
                className={`rounded-lg p-4 ${
                  newStock !== null && newStock < 0
                    ? "bg-red-50 border border-red-200"
                    : "bg-green-50 border border-green-200"
                }`}
              >
                <div className="flex items-center gap-2">
                  {getTransactionIcon(transactionType)}
                  <span className="font-medium">
                    {item.currentStock} →{" "}
                    <span
                      className={
                        newStock !== null && newStock < 0
                          ? "text-red-600"
                          : "text-green-600"
                      }
                    >
                      {newStock}
                    </span>
                  </span>
                  {newStock !== null && newStock < 0 && (
                    <span className="text-red-600 text-sm">
                      (Cannot go below 0)
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Reason */}
            <div className="space-y-2">
              <Label htmlFor="reason">
                Reason
                {(transactionType === "adjustment" || transactionType === "waste") && (
                  <span className="text-red-500 ml-1">*</span>
                )}
              </Label>
              <Input
                id="reason"
                {...register("reason", {
                  required:
                    transactionType === "adjustment" || transactionType === "waste"
                      ? "Reason is required for this transaction type"
                      : false,
                })}
                placeholder={
                  transactionType === "adjustment"
                    ? "e.g., Physical count correction, Damaged goods found"
                    : transactionType === "waste"
                      ? "e.g., Expired, Damaged, Recalled"
                      : "e.g., Purchase order #12345"
                }
                autoComplete="off"
              />
              {errors.reason && (
                <p className="text-sm text-red-600">{errors.reason.message}</p>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                {...register("notes")}
                placeholder="Additional notes about this transaction..."
                rows={2}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!isValidTransaction}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 disabled:opacity-50"
              >
                <Save className="w-4 h-4 mr-2" />
                Process Transaction
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
